<template>
  <div>
    <!-- Page hero banner -->
    <div class="invoice-hero mb-1">
      <div class="invoice-hero__bg"/>
      <div class="invoice-hero__content">
        <div>
          <h3 class="invoice-hero__title mb-25">{{ msg('Invoices') }}</h3>
          <p class="invoice-hero__subtitle mb-0">{{ msg('Manage and track all building invoices') }}</p>
        </div>
        <!-- Stat pills -->
        <div class="d-flex flex-wrap mt-1" style="gap:10px">
          <div class="invoice-hero__stat">
            <feather-icon icon="FileTextIcon" size="14"/>
            <span class="font-weight-bolder">{{ totalStats.total }}</span> {{ msg('total') }}
          </div>
          <div class="invoice-hero__stat invoice-hero__stat--warning">
            <feather-icon icon="ClockIcon" size="14"/>
            <span class="font-weight-bolder">{{ totalStats.open }}</span> {{ msg('open') }}
          </div>
          <div class="invoice-hero__stat invoice-hero__stat--success">
            <feather-icon icon="CheckCircleIcon" size="14"/>
            <span class="font-weight-bolder">{{ totalStats.paid }}</span> {{ msg('paid') }}
          </div>
          <div v-if="totalStats.draft" class="invoice-hero__stat invoice-hero__stat--secondary">
            <feather-icon icon="EditIcon" size="14"/>
            <span class="font-weight-bolder">{{ totalStats.draft }}</span> {{ msg('drafts') }}
          </div>
        </div>
      </div>
      <!-- Floating invoice illustration -->
      <svg class="invoice-hero__illustration" viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="10" y="5" width="80" height="105" rx="6" fill="rgba(255,255,255,.15)" stroke="rgba(255,255,255,.25)" stroke-width="1.5"/>
        <rect x="22" y="22" width="36" height="3" rx="1.5" fill="rgba(255,255,255,.4)"/>
        <rect x="22" y="30" width="50" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
        <rect x="22" y="38" width="44" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
        <rect x="22" y="46" width="48" height="2.5" rx="1.25" fill="rgba(255,255,255,.25)"/>
        <rect x="22" y="58" width="50" height="2.5" rx="1.25" fill="rgba(255,255,255,.15)"/>
        <rect x="22" y="66" width="38" height="2.5" rx="1.25" fill="rgba(255,255,255,.15)"/>
        <rect x="22" y="78" width="30" height="6" rx="3" fill="rgba(255,255,255,.3)"/>
        <line x1="22" y1="92" x2="72" y2="92" stroke="rgba(255,255,255,.2)" stroke-width="1" stroke-dasharray="3 2"/>
        <text x="22" y="103" font-size="8" fill="rgba(255,255,255,.35)" font-family="sans-serif">TOTAL</text>
        <text x="55" y="103" font-size="9" font-weight="bold" fill="rgba(255,255,255,.45)" font-family="sans-serif">€ --</text>
        <!-- Floating coin accents -->
        <circle cx="100" cy="25" r="10" fill="rgba(255,255,255,.08)" class="invoice-hero__float1"/>
        <circle cx="105" cy="90" r="7" fill="rgba(255,255,255,.06)" class="invoice-hero__float2"/>
        <circle cx="5" cy="100" r="12" fill="rgba(255,255,255,.05)" class="invoice-hero__float3"/>
      </svg>
    </div>

    <!-- Filters -->
    <b-card no-body class="mb-1">
      <b-card-body class="pb-0 pt-75">
        <b-row>
          <b-col cols="12" md="4" class="mb-75">
            <b-form-input v-model="searchQuery" size="sm" :placeholder="msg('Search invoices...')" debounce="300"/>
          </b-col>
          <b-col cols="6" md="3" class="mb-75">
            <v-select v-model="filterStatus" :options="statusOptions" :reduce="s=>s.value" label="label"
                      :placeholder="msg('All statuses')" :clearable="true" class="select-size-sm"/>
          </b-col>
          <b-col cols="6" md="3" class="mb-75">
            <v-select v-model="filterBranchId" :options="branchOptions" :reduce="b=>b.id" label="name"
                      :placeholder="msg('All branches')" :clearable="true" class="select-size-sm"/>
          </b-col>
          <b-col cols="12" md="2" class="mb-75 text-md-right">
            <b-button v-if="hasFilters" variant="flat-secondary" size="sm" @click="clearFilters">
              <feather-icon icon="XIcon" size="13" class="mr-25"/>{{ msg('Clear') }}
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Collapse / Expand all -->
    <div v-if="!loading && branchGroups.length" class="d-flex align-items-center mb-75" style="gap:6px">
      <b-button variant="flat-secondary" size="sm" @click="collapseAll">
        <feather-icon icon="MinimizeIcon" size="13" class="mr-25"/>{{ msg('Collapse all') }}
      </b-button>
      <b-button variant="flat-secondary" size="sm" @click="expandAll">
        <feather-icon icon="MaximizeIcon" size="13" class="mr-25"/>{{ msg('Expand all') }}
      </b-button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-3">
      <b-spinner variant="primary"/>
    </div>

    <!-- Empty -->
    <b-card v-else-if="branchGroups.length===0" class="text-center py-2">
      <feather-icon icon="InboxIcon" size="40" class="text-muted mb-1"/>
      <p class="text-muted mb-0">{{ msg('No invoices found') }}</p>
    </b-card>

    <!-- Grouped by branch -->
    <div v-else v-for="group in branchGroups" :key="group.branchId" class="mb-2">

      <!-- Branch header (clickable to collapse) -->
      <div class="d-flex align-items-center mb-75 cursor-pointer" style="gap:8px" @click="toggleBranch(group.branchId)">
        <feather-icon :icon="isBranchCollapsed(group.branchId)?'ChevronRightIcon':'ChevronDownIcon'" size="14" class="text-primary"/>
        <feather-icon icon="GitBranchIcon" size="14" class="text-primary"/>
        <h6 class="mb-0 text-primary">{{ group.branchName }}</h6>
        <small class="text-muted">{{ group.buildings.length }} {{ msg('buildings') }} · {{ group.totalInvoices }} {{ msg('invoices') }}</small>
        <b-button variant="flat-secondary" size="sm" class="ml-auto btn-icon p-25" @click.stop="toggleBranch(group.branchId)"
                  v-b-tooltip.hover="isBranchCollapsed(group.branchId)?msg('Expand'):msg('Collapse')">
          <feather-icon :icon="isBranchCollapsed(group.branchId)?'PlusSquareIcon':'MinusSquareIcon'" size="14"/>
        </b-button>
      </div>

      <b-collapse :visible="!isBranchCollapsed(group.branchId)">
        <b-card v-for="b in group.buildings" :key="b.id" no-body class="mb-75">

          <!-- Building header (clickable to collapse) -->
          <b-card-header class="d-flex align-items-center justify-content-between py-75 flex-wrap cursor-pointer" style="gap:8px"
                         @click="toggleBuilding(b.id)">
            <div class="d-flex align-items-center" style="gap:6px">
              <feather-icon :icon="isBuildingCollapsed(b.id)?'ChevronRightIcon':'ChevronDownIcon'" size="13"/>
              <h6 class="mb-0">{{ b.address || b.company || b.id }}</h6>
            </div>
            <div class="d-flex align-items-center building-actions-wrap">
              <small class="text-muted mr-50">
                {{ b._invoices.length }} {{ msg('invoices') }}
                <span v-if="b._openAmt>0" class="text-warning ml-50">{{ formatAmount(b._openAmt,'EUR') }} {{ msg('open') }}</span>
              </small>
              <div v-if="isAdmin" class="building-action-btns">
                <b-button v-if="b._invoices.length>0" variant="outline-primary" size="sm" class="building-btn building-btn--primary"
                          :disabled="!!buildingLoading[b.id]" @click.stop="openCreateInvoice(b)">
                  <feather-icon icon="EditIcon" size="13" class="mr-50"/>
                  {{ msg('Update invoice') }}
                </b-button>
                <b-button variant="outline-success" size="sm" class="building-btn building-btn--success"
                          :disabled="!!buildingLoading[b.id]" @click.stop="openRegenerate(b)">
                  <b-spinner v-if="buildingLoading[b.id]==='regen'" small type="grow" class="mr-50"/>
                  <feather-icon v-else icon="CalendarIcon" size="13" class="mr-50"/>
                  {{ msg('Generate for period') }}
                </b-button>
                <b-button variant="outline-danger" size="sm" class="building-btn building-btn--danger"
                          :disabled="b._invoices.length===0||!!buildingLoading[b.id]" @click.stop="confirmDeleteAll(b)">
                  <b-spinner v-if="buildingLoading[b.id]==='deleteAll'" small type="grow" class="mr-50"/>
                  <feather-icon v-else icon="Trash2Icon" size="13" class="mr-50"/>
                  {{ msg('Remove all') }}
                </b-button>
              </div>
            </div>
          </b-card-header>

          <b-collapse :visible="!isBuildingCollapsed(b.id)">

            <!-- Invoice table with pagination -->
            <b-table
                v-if="b._invoices.length"
                :items="b._invoices"
                :fields="fields"
                :per-page="perPage"
                :current-page="getPage(b.id)"
                responsive
                hover
                small
                thead-class="text-uppercase"
                class="mb-0"
                table-class="invoice-table-fixed"
                :tbody-tr-class="rowClass"
            >
              <template #cell(invoiceStatus)="{item}">
                <b-badge :variant="statusVariant(item.invoiceStatus)" pill>{{ item.invoiceStatus }}</b-badge>
              </template>

              <template #cell(title)="{item}">
                <span class="font-weight-bold invoice-title-text" :title="item.title || msg('Invoice')">{{ item.title || msg('Invoice') }}</span>
              </template>

              <template #cell(totalAmount)="{item}">
                <span class="font-weight-bolder">{{ formatAmount(item.totalAmount, item.currency) }}</span>
              </template>

              <template #cell(creationDate)="{item}">
                <small class="text-muted">{{ formatDate(item.creationDate) }}</small>
              </template>

              <template #cell(dueDate)="{item}">
                <small :class="isOverdue(item)?'text-danger font-weight-bold':'text-muted'">
                  {{ formatDate(item.dueDate) }}
                  <feather-icon v-if="isOverdue(item)" icon="AlertCircleIcon" size="11"/>
                </small>
              </template>

              <template #cell(actions)="{item}">
                <div class="invoice-row-actions">
                  <!-- Fixed slots: same width per row so OPEN / PAID / etc. stay aligned -->
                  <div class="invoice-action-slot">
                    <b-button variant="outline-primary" size="sm" class="invoice-row-btn" @click="openInvoiceModal(b,item)" v-b-tooltip.hover="msg('Open invoice')">
                      <feather-icon icon="EyeIcon" size="14"/>
                    </b-button>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="item.links&&item.links.pdfLink" variant="outline-secondary" size="sm" class="invoice-row-btn" @click="openLink(item.links.pdfLink)" v-b-tooltip.hover="msg('Download PDF')">
                      <feather-icon icon="DownloadIcon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="item.invoiceStatus==='OPEN'" variant="outline-info" size="sm" class="invoice-row-btn"
                              :disabled="!!actionLoading[item.invoiceId]" @click="openPaymentLink(item)" v-b-tooltip.hover="msg('Payment link')">
                      <b-spinner v-if="actionLoading[item.invoiceId]==='pay'" small type="grow"/>
                      <feather-icon v-else icon="CreditCardIcon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="isAdmin&&item.invoiceStatus!=='PAID'&&item.invoiceStatus!=='DELETED'" variant="outline-secondary" size="sm" class="invoice-row-btn"
                              :disabled="!!actionLoading[item.invoiceId]" @click="refreshLinks(item)" v-b-tooltip.hover="msg('Refresh link')">
                      <b-spinner v-if="actionLoading[item.invoiceId]==='refresh'" small type="grow"/>
                      <feather-icon v-else icon="RefreshCwIcon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="isAdmin&&isOverdue(item)&&item.invoiceStatus!=='PAID'" variant="outline-warning" size="sm" class="invoice-row-btn"
                              :disabled="!!actionLoading[item.invoiceId]" @click="recreateForPeriod(b,item)" v-b-tooltip.hover="msg('Recreate for period')">
                      <b-spinner v-if="actionLoading[item.invoiceId]==='recreate'" small type="grow"/>
                      <feather-icon v-else icon="RotateCcwIcon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="isAdmin&&item.invoiceStatus==='OPEN'" variant="outline-success" size="sm" class="invoice-row-btn"
                              :disabled="!!actionLoading[item.invoiceId]" @click="markAsPaid(b,item)" v-b-tooltip.hover="msg('Mark as paid')">
                      <b-spinner v-if="actionLoading[item.invoiceId]==='paid'" small type="grow"/>
                      <feather-icon v-else icon="CheckCircleIcon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                  <div class="invoice-action-slot">
                    <b-button v-if="isAdmin&&item.invoiceStatus!=='PAID'" variant="outline-danger" size="sm" class="invoice-row-btn"
                              :disabled="!!actionLoading[item.invoiceId]" @click="confirmDelete(b,item)" v-b-tooltip.hover="msg('Remove invoice')">
                      <b-spinner v-if="actionLoading[item.invoiceId]==='delete'" small type="grow"/>
                      <feather-icon v-else icon="Trash2Icon" size="14"/>
                    </b-button>
                    <span v-else class="invoice-action-spacer" aria-hidden="true"/>
                  </div>
                </div>
              </template>
            </b-table>

            <!-- Pagination (only if more than perPage) -->
            <b-card-footer v-if="b._invoices.length > perPage" class="py-50 d-flex align-items-center justify-content-between">
              <small class="text-muted">
                {{ msg('Showing') }} {{ ((getPage(b.id)-1)*perPage)+1 }}–{{ Math.min(getPage(b.id)*perPage, b._invoices.length) }}
                {{ msg('of') }} {{ b._invoices.length }}
              </small>
              <b-pagination
                  :value="getPage(b.id)"
                  @input="setPage(b.id, $event)"
                  :total-rows="b._invoices.length"
                  :per-page="perPage"
                  size="sm"
                  class="mb-0"
                  first-number
                  last-number
                  pills
              />
            </b-card-footer>

            <b-card-body v-if="b._invoices.length===0" class="text-center py-2">
              <small class="text-muted">{{ msg('No invoices yet') }}</small>
            </b-card-body>
          </b-collapse>
        </b-card>
      </b-collapse>
    </div>

    <!-- Modals -->
    <building-invoice-modal ref="invoiceModal" :building="selectedBuilding" @invoice="onInvoiceChange"/>

    <b-modal ref="deleteModal" :title="msg('Delete Invoice')" ok-variant="danger"
             :ok-title="msg('Delete')" :cancel-title="msg('Cancel')" centered @ok="executeDelete">
      <div class="text-center py-1">
        <feather-icon icon="AlertTriangleIcon" size="32" class="text-danger mb-75"/>
        <p class="mb-0" v-if="pendingDelete"><strong>{{ pendingDelete.invoice.title }}</strong></p>
        <small class="text-muted">{{ msg('This cannot be undone.') }}</small>
      </div>
    </b-modal>

    <b-modal ref="deleteAllModal" :title="msg('Delete All Invoices')" ok-variant="danger"
             :ok-title="msg('Delete All')" :cancel-title="msg('Cancel')" centered @ok="executeDeleteAll">
      <div class="text-center py-1">
        <feather-icon icon="AlertOctagonIcon" size="32" class="text-danger mb-75"/>
        <p class="mb-25" v-if="pendingDeleteAll"><strong>{{ pendingDeleteAll.address||pendingDeleteAll.company||pendingDeleteAll.id }}</strong></p>
        <p class="text-danger small mb-0" v-if="pendingDeleteAll">{{ (pendingDeleteAll._invoices||[]).length }} {{ msg('invoices will be deleted') }}</p>
      </div>
    </b-modal>

    <b-modal ref="regenModal" :title="msg('Generate Invoice')" ok-variant="success"
             :ok-title="msg('Generate')" :cancel-title="msg('Cancel')" centered
             :ok-disabled="!regenCanSubmit" @ok="executeRegenerate">
      <p class="small mb-1" v-if="regenBuilding">
        <strong>{{ regenBuilding.address||regenBuilding.company||regenBuilding.id }}</strong>
      </p>
      <b-row>
        <b-col cols="6">
          <b-form-group :label="msg('Month')" label-size="sm">
            <v-select v-model="regenMonth" :options="monthOptions" :reduce="o=>o.value" label="label" :clearable="false"/>
          </b-form-group>
        </b-col>
        <b-col cols="6">
          <b-form-group :label="msg('Year')" label-size="sm">
            <v-select v-model="regenYear" :options="yearOptions" :reduce="o=>o.value" label="label" :clearable="false"/>
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import BuildingInvoiceModal from '@/components/modals/BuildingInvoiceModal.vue'

export default {
  name: 'BuildingInvoices',
  components: { BuildingInvoiceModal },
  data() {
    return {
      loading: false,
      buildings: [],
      searchQuery: '',
      filterStatus: null,
      filterBranchId: null,
      actionLoading: {},
      buildingLoading: {},
      selectedBuilding: null,
      pendingDelete: null,
      pendingDeleteAll: null,
      regenBuilding: null,
      regenMonth: null,
      regenYear: null,
      collapsedBranches: {},
      collapsedBuildings: {},
      buildingPages: {},
      perPage: 15,
    }
  },
  computed: {
    ...mapGetters('translator', ['msg']),
    ...mapGetters('branches', ['allBranches', 'isLoaded']),
    ...mapGetters('user', ['myAdminStatus']),
    isAdmin() { return true },
    hasFilters() { return this.searchQuery || this.filterStatus || this.filterBranchId },
    statusOptions() {
      return [{ value:'DRAFT',label:'Draft'},{ value:'OPEN',label:'Open'},{ value:'PAID',label:'Paid'},{ value:'VOID',label:'Void'}]
    },
    branchOptions() { return (this.allBranches||[]).map(b=>({id:b.id,name:b.name})) },
    monthOptions() {
      return ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
        .map((l, i) => ({ value: i + 1, label: l }))
    },
    yearOptions() {
      const y = new Date().getFullYear()
      return Array.from({ length: 6 }, (_, i) => ({ value: y - i, label: String(y - i) }))
    },
    fields() {
      return [
        { key:'invoiceStatus', label:this.msg('Status'), thStyle:{ width:'90px' } },
        { key:'title', label:this.msg('Title'), thStyle:{ width:'140px' }, tdClass:'invoice-title-td' },
        { key:'totalAmount', label:this.msg('Amount'), thStyle:{ width:'100px' }, thClass:'text-right', tdClass:'text-right' },
        { key:'creationDate', label:this.msg('Created'), thStyle:{ width:'95px' } },
        { key:'dueDate', label:this.msg('Due'), thStyle:{ width:'95px' } },
        { key:'actions', label:'', thStyle:{ width:'320px', minWidth:'320px' }, tdClass:'text-right invoice-actions-cell' },
      ]
    },
    branchNameMap() {
      const m={}; (this.allBranches||[]).forEach(b=>{m[b.id]=b.name}); return m
    },
    displayBuildings() {
      return this.buildings
        .map(building => {
          const bid = building.buid || 0
          if (this.filterBranchId != null && bid !== this.filterBranchId) return null
          const invoices = (building.buildingInvoices || []).filter(inv => {
            const ok1 = !this.filterStatus || inv.invoiceStatus === this.filterStatus
            const ok2 = !this.searchQuery ||
              (inv.title||'').toLowerCase().includes(this.searchQuery.toLowerCase()) ||
              (inv.invoiceId||'').toLowerCase().includes(this.searchQuery.toLowerCase())
            return ok1 && ok2
          })
          if (invoices.length === 0 && !this.isAdmin) return null
          return {
            ...building,
            _branchId: bid,
            _branchName: this.branchNameMap[bid] || `Branch #${bid}`,
            _invoices: invoices,
            _openAmt: invoices.filter(i=>i.invoiceStatus==='OPEN').reduce((s,i)=>s+(i.totalAmount||0),0),
          }
        })
        .filter(Boolean)
        .sort((a,b) => (a._branchId - b._branchId) || (a.address||'').localeCompare(b.address||''))
    },
    branchGroups() {
      const map = {}
      for (const b of this.displayBuildings) {
        const bid = b._branchId
        if (!map[bid]) map[bid] = { branchId: bid, branchName: b._branchName, buildings: [], totalInvoices: 0 }
        map[bid].buildings.push(b)
        map[bid].totalInvoices += b._invoices.length
      }
      return Object.values(map).sort((a, b) => a.branchId - b.branchId)
    },
    totalStats() {
      const all = this.buildings.flatMap(b => b.buildingInvoices || [])
      return {
        total: all.length,
        open:  all.filter(i => i.invoiceStatus === 'OPEN').length,
        paid:  all.filter(i => i.invoiceStatus === 'PAID').length,
        draft: all.filter(i => i.invoiceStatus === 'DRAFT').length,
      }
    },
    regenCanSubmit() {
      return !!(this.regenMonth && this.regenYear)
    },
  },
  created() {
    if (!this.isLoaded) this.refreshBranches({ filter: {} }).finally(() => this.loadBuildings())
    else this.loadBuildings()
  },
  methods: {
    ...mapActions('building', ['getAllBuildings','deleteInvoice','markInvoiceAsPaid','refreshInvoiceLinks','deleteAllBuildingInvoices','regenerateBuildingInvoice']),
    ...mapActions('branches', { refreshBranches: 'refresh' }),
    loadBuildings(preserveExpanded = false) {
      const expandedBranchIds = preserveExpanded ? this.branchGroups.filter(g => !this.isBranchCollapsed(g.branchId)).map(g => g.branchId) : []
      const expandedBuildingIds = preserveExpanded ? this.displayBuildings.filter(b => !this.isBuildingCollapsed(b.id)).map(b => b.id) : []
      this.loading = true
      this.getAllBuildings({ page: 0, amount: 500, filter: {}, sortcolumn: 'company', sortby: 'asc' })
        .then(a => {
          this.buildings = a['Buildings'] || []
          this.$nextTick(() => {
            if (preserveExpanded && (expandedBranchIds.length || expandedBuildingIds.length)) {
              expandedBranchIds.forEach(id => { this.$set(this.collapsedBranches, id, false) })
              expandedBuildingIds.forEach(id => { this.$set(this.collapsedBuildings, id, false) })
            } else {
              this.collapseAllBranches()
            }
          })
        })
        .finally(() => { this.loading = false })
    },
    statusVariant(s) { return {DRAFT:'light-secondary',OPEN:'light-warning',PAID:'light-success',VOID:'light-danger',DELETED:'light-danger'}[(s||'').toUpperCase()]||'light-secondary' },
    formatAmount(a,c) { if(a==null) return '—'; return `${c==='USD'?'$':c==='GBP'?'£':'€'}${parseFloat(a).toFixed(2)}` },
    formatDate(d) { if(!d) return '—'; try { const dt=new Date(d); return isNaN(dt.getTime())?'—':dt.toLocaleDateString('de-DE',{day:'2-digit',month:'2-digit',year:'numeric'}) } catch{return '—'} },
    isOverdue(inv) { return inv.dueDate && inv.invoiceStatus!=='PAID' && new Date(inv.dueDate)<new Date() },
    openLink(u) { if(u) window.open(u,'_blank') },
    clearFilters() { this.searchQuery=''; this.filterStatus=null; this.filterBranchId=null },
    toggleBranch(id) { this.$set(this.collapsedBranches, id, !this.collapsedBranches[id]) },
    isBranchCollapsed(id) { return !!this.collapsedBranches[id] },
    toggleBuilding(id) { this.$set(this.collapsedBuildings, id, !this.collapsedBuildings[id]) },
    isBuildingCollapsed(id) { return !!this.collapsedBuildings[id] },
    collapseAllBranches() {
      this.branchGroups.forEach(g => { this.$set(this.collapsedBranches, g.branchId, true) })
    },
    collapseAll() {
      this.collapseAllBranches()
      this.displayBuildings.forEach(b => { this.$set(this.collapsedBuildings, b.id, true) })
    },
    expandAll() { this.collapsedBranches = {}; this.collapsedBuildings = {} },
    getPage(buildingId) { return this.buildingPages[buildingId] || 1 },
    setPage(buildingId, page) { this.$set(this.buildingPages, buildingId, page) },
    rowClass(item) { if(!item) return ''; if(item.invoiceStatus==='PAID') return 'row-paid'; if(this.isOverdue(item)) return 'row-overdue'; return '' },
    openInvoiceModal(b,inv) { this.selectedBuilding=b; this.$nextTick(()=>{this.$refs.invoiceModal.showInvoice(inv.invoiceId)}) },
    openCreateInvoice(b) { const t=b||this.buildings[0]; if(!t)return; this.selectedBuilding=t; this.$nextTick(()=>{this.$refs.invoiceModal.showModal(t.id)}) },
    onInvoiceChange() { this.loadBuildings(true) },
    markAsPaid(b,inv) { this.$set(this.actionLoading,inv.invoiceId,'paid'); this.markInvoiceAsPaid(inv.invoiceId).then(()=>this.loadBuildings(true)).finally(()=>this.$set(this.actionLoading,inv.invoiceId,false)) },
    refreshLinks(inv) { this.$set(this.actionLoading,inv.invoiceId,'refresh'); this.refreshInvoiceLinks(inv.invoiceId).then(()=>this.loadBuildings(true)).finally(()=>this.$set(this.actionLoading,inv.invoiceId,false)) },
    openPaymentLink(inv) {
      if (inv.links && inv.links.paymentLink) {
        this.openLink(inv.links.paymentLink)
        return
      }
      this.$set(this.actionLoading, inv.invoiceId, 'pay')
      this.refreshInvoiceLinks(inv.invoiceId)
        .then((building) => {
          const bi = (building.buildingInvoices || []).find((x) => x.invoiceId === inv.invoiceId)
          if (bi && bi.links && bi.links.paymentLink) {
            this.openLink(bi.links.paymentLink)
          }
          this.loadBuildings(true)
        })
        .finally(() => this.$set(this.actionLoading, inv.invoiceId, false))
    },
    confirmDelete(b,inv) { this.pendingDelete={building:b,invoice:inv}; this.$refs.deleteModal.show() },
    executeDelete() { if(!this.pendingDelete)return; const id=this.pendingDelete.invoice.invoiceId; this.$set(this.actionLoading,id,'delete'); this.deleteInvoice(id).then(()=>this.loadBuildings(true)).finally(()=>{this.$set(this.actionLoading,id,false);this.pendingDelete=null}) },
    confirmDeleteAll(b) { this.pendingDeleteAll=b; this.$refs.deleteAllModal.show() },
    executeDeleteAll() { if(!this.pendingDeleteAll)return; const id=this.pendingDeleteAll.id; this.$set(this.buildingLoading,id,'deleteAll'); this.deleteAllBuildingInvoices(id).then(()=>this.loadBuildings(true)).finally(()=>{this.$set(this.buildingLoading,id,false);this.pendingDeleteAll=null}) },
    recreateForPeriod(b,inv) { const d=inv.creationDate?new Date(inv.creationDate):null; if(!d||isNaN(d.getTime()))return; this.$set(this.actionLoading,inv.invoiceId,'recreate'); this.regenerateBuildingInvoice({buildingId:b.id,month:d.getMonth()+1,year:d.getFullYear()}).then(()=>this.loadBuildings(true)).catch(()=>{}).finally(()=>this.$set(this.actionLoading,inv.invoiceId,false)) },
    openRegenerate(b) {
      if (!b) return
      this.regenBuilding = b
      const n = new Date()
      this.regenMonth = n.getMonth() === 0 ? 12 : n.getMonth()
      this.regenYear = n.getMonth() === 0 ? n.getFullYear() - 1 : n.getFullYear()
      this.$refs.regenModal.show()
    },
    executeRegenerate() { if(!this.regenBuilding||!this.regenMonth||!this.regenYear)return; const id=this.regenBuilding.id; this.$set(this.buildingLoading,id,'regen'); this.regenerateBuildingInvoice({buildingId:id,month:this.regenMonth,year:this.regenYear}).then(()=>this.loadBuildings(true)).catch(()=>{}).finally(()=>{this.$set(this.buildingLoading,id,false);this.regenBuilding=null}) },
  },
}
</script>

<style scoped>
.row-paid { opacity: .55; }
.cursor-pointer { cursor: pointer; }

/* ── Hero banner ── */
.invoice-hero {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  min-height: 130px;
}
.invoice-hero__bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 50%, #ce9ffc 100%);
  z-index: 0;
}
.invoice-hero__content {
  position: relative;
  z-index: 1;
  padding: 20px 24px;
}
.invoice-hero__title {
  color: #fff;
  font-weight: 700;
  letter-spacing: -.3px;
}
.invoice-hero__subtitle {
  color: rgba(255,255,255,.75);
  font-size: .85rem;
}
.invoice-hero__btn {
  background: rgba(255,255,255,.2) !important;
  border: 1px solid rgba(255,255,255,.3) !important;
  color: #fff !important;
  backdrop-filter: blur(4px);
  transition: background .2s;
}
.invoice-hero__btn:hover {
  background: rgba(255,255,255,.35) !important;
}

/* Stat pills */
.invoice-hero__stat {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: .78rem;
  color: #fff;
  background: rgba(255,255,255,.15);
  backdrop-filter: blur(4px);
}
.invoice-hero__stat--warning { background: rgba(255,159,67,.3); }
.invoice-hero__stat--success { background: rgba(40,199,111,.3); }
.invoice-hero__stat--secondary { background: rgba(255,255,255,.1); }

/* Floating SVG illustration */
.invoice-hero__illustration {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 110px;
  height: auto;
  opacity: .7;
  z-index: 0;
  pointer-events: none;
}

/* Gentle floating animation on the accent circles */
.invoice-hero__float1 { animation: heroFloat 6s ease-in-out infinite; }
.invoice-hero__float2 { animation: heroFloat 8s ease-in-out 1s infinite; }
.invoice-hero__float3 { animation: heroFloat 7s ease-in-out 2s infinite; }

@keyframes heroFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* Hide illustration on small screens to keep things clean */
@media (max-width: 576px) {
  .invoice-hero__illustration { display: none; }
  .invoice-hero { min-height: auto; }
}

/* ── Building header action buttons ── */
.building-actions-wrap {
  gap: 10px;
  flex-wrap: wrap;
}
.building-action-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.building-btn {
  border-radius: 6px;
  font-weight: 600;
  padding: 0.4rem 0.75rem;
  transition: box-shadow 0.15s ease, background-color 0.15s ease;
}
.building-btn:hover:not(:disabled) {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.building-btn .mr-50 { margin-right: 0.35rem; }

/* ── Invoice row action buttons (fixed grid: same layout for OPEN / PAID / …) ── */
.invoice-actions-cell {
  vertical-align: middle !important;
}
.invoice-title-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.invoice-row-actions {
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  min-width: 280px;
}
.invoice-action-slot {
  flex: 0 0 36px;
  width: 36px;
  min-width: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.invoice-action-spacer {
  display: block;
  width: 36px;
  min-width: 36px;
  height: 34px;
}
.invoice-row-btn {
  min-width: 36px;
  width: 36px;
  padding: 0.35rem;
  border-radius: 6px;
  transition: box-shadow 0.12s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.invoice-row-btn:hover:not(:disabled) {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
</style>

<style>
/* b-table renders <table> inside child component — keep layout rules unscoped */
table.table.invoice-table-fixed {
  table-layout: fixed;
  width: 100%;
}
td.invoice-title-td {
  overflow: hidden;
  vertical-align: middle !important;
}

.row-overdue td { background: rgba(234,84,85,.06) !important; }
.dark-layout .row-overdue td { background: rgba(234,84,85,.1) !important; }

/* Dark-mode hero adjustments */
.dark-layout .invoice-hero__bg {
  background: linear-gradient(135deg, #4839a0 0%, #7367f0 50%, #9e84e9 100%);
}
.dark-layout .invoice-hero__btn {
  background: rgba(255,255,255,.12) !important;
  border-color: rgba(255,255,255,.2) !important;
}
</style>
